import { CdkDrag, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { SitesService } from 'src/app/mobile/shared/sites.service';
import { Choice } from 'src/app/shared/components/select/choice.model';
import { BasicService } from 'src/app/shared/services/basic.service';
import { Template, WhatsappTemplate } from '../../shared';
import { WhatsappTemplateButtonTypeEnum } from '../../shared/models/whatsapp-template-button-type.enum';
import { WhatsappTemplatesService } from '../../whatsapp-templates.service';
import { UrlValidator } from './shared/button-url-example.validator';

@Component({
  selector: 'app-whatsapp-template-languages',
  templateUrl: './whatsapp-template-languages.component.html',
  styleUrls: ['./whatsapp-template-languages.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class WhatsappTemplateLanguagesComponent implements OnInit, OnDestroy {
  // {code, template} code means the language code related to the tab, template is the template regarding the selected language.
  @Input() form: FormGroup;
  @Input() create: boolean;
  @Input() agentShortcuts: Choice[] = [];
  @Input() template: WhatsappTemplate;

  isReadOnly = false;

  buttonsGrouped = new FormGroup({
    quickReply: new FormArray([]),
    url: new FormArray([]),
    phoneNumber: new FormArray([]),
    copyCode: new FormArray([]),
  });

  get buttons() {
    return this.form?.get('template.buttons') as FormArray;
  }

  get quickReplyButtons() {
    return this.buttonsGrouped?.get('quickReply') as FormArray;
  }

  get urlButtons() {
    return this.buttonsGrouped?.get('url') as FormArray;
  }

  get phoneNumberButtons() {
    return this.buttonsGrouped?.get('phoneNumber') as FormArray;
  }

  get copyCodeButtons() {
    return this.buttonsGrouped?.get('copyCode') as FormArray;
  }

  get body() {
    return this.form?.get('template.body') as FormControl;
  }

  get placeholders() {
    return this.form?.get('template.placeholders') as FormGroup;
  }

  WhatsappTemplateButtonTypeEnum = WhatsappTemplateButtonTypeEnum;

  templates: Template[];
  cannotAddButton: boolean;
  cannotAddUrlButton: boolean;
  cannotAddPhoneNumberButton: boolean;
  cannotAddCopyCodeButton: boolean;
  buttonsTypesSet = new Set();

  placeholdersControls: string[] = [];
  urlPlaceholders = {};
  headerType: 'text' | 'media' = 'text';
  limitations: {
    maxLength: any;
    maxNumber: any;
  };

  phoneNumberObjectMap = new Map();
  @ViewChild('phoneInput') phoneInputElement: ElementRef;

  userLanguage = this.alcmeonContextService.lang;
  urlButtonTrackLinkLabels: Choice[] = [];

  quickRepliesSection = {
    type: WhatsappTemplateButtonTypeEnum.QUICK_REPLY,
    formArray: this.quickReplyButtons,
    titleKey: 'parameters/whatsapp-templates/content/buttons/add-quick-reply',
    titleIcon: 'abc',
  };
  urlsSection = {
    type: WhatsappTemplateButtonTypeEnum.URL,
    formArray: this.urlButtons,
    titleKey: 'parameters/whatsapp-templates/content/buttons/add-url',
    titleIcon: 'open_in_new',
  };
  phoneNumberSection = {
    type: WhatsappTemplateButtonTypeEnum.PHONE_NUMBER,
    formArray: this.phoneNumberButtons,
    titleKey: 'parameters/whatsapp-templates/content/buttons/add-phone-number',
    titleIcon: 'call',
  };
  copyCodeSection = {
    type: WhatsappTemplateButtonTypeEnum.COPY_CODE,
    formArray: this.copyCodeButtons,
    titleKey: 'parameters/whatsapp-templates/content/buttons/add-copy-code',
    titleIcon: 'content_copy',
  };
  /**
   * Especially used for the buttons grouped by type and the drag and drop mechanism.
   */
  sections = [this.quickRepliesSection, this.urlsSection, this.phoneNumberSection, this.copyCodeSection];

  isWhatsappCloudAPI: boolean;
  limitedTimeOfferEnabled: boolean;
  bodyLTOMaxlength: number;
  trackLinkLabelsManagementUrl: string;

  private subscriptions = new Subscription();

  constructor(
    private whatsappTemplatesService: WhatsappTemplatesService,
    private alcmeonContextService: AlcmeonContextService,
    private basicService: BasicService,
    private sitesService: SitesService
  ) {
    this.limitations = this.whatsappTemplatesService.getLimitations();
    const path = `/#/${alcmeonContextService.companyId}/fuse/parameters/track-link-labels`;
    this.trackLinkLabelsManagementUrl = basicService.fullURLForRoutePath(path);

    whatsappTemplatesService
      .getUrlButtonTrackLinkLabels()
      .subscribe(values => (this.urlButtonTrackLinkLabels = values));
  }

  ngOnInit() {
    this.handleTemplateFormForWhatsappCloudAPIAccount();
    this.subscriptions.add(
      this.buttonsGrouped.valueChanges.pipe(debounceTime(100)).subscribe(() => {
        const { quickReply, url, phoneNumber, copyCode } = this.buttonsGrouped.getRawValue(); // getRawValue instead of value to avoid undefined result (form disabled in readonly)
        const buttons = [].concat(quickReply).concat(url).concat(phoneNumber).concat(copyCode);

        this.cannotAddButton = this.buttonsGrouped.invalid || buttons.length > this.limitations.maxNumber.button - 1;

        let phoneNumberTypeNumber = 0;
        let urlTypeNumber = 0;
        let copyCodeTypeNumber = 0;
        buttons.forEach(({ type }) => {
          if (type === WhatsappTemplateButtonTypeEnum.PHONE_NUMBER) {
            phoneNumberTypeNumber++;
          } else if (type === WhatsappTemplateButtonTypeEnum.URL) {
            urlTypeNumber++;
          } else if (type === WhatsappTemplateButtonTypeEnum.COPY_CODE) {
            copyCodeTypeNumber++;
          }
        });

        this.cannotAddUrlButton = this.cannotAddButton || urlTypeNumber >= 2;
        this.cannotAddPhoneNumberButton = this.cannotAddButton || phoneNumberTypeNumber >= 1;
        this.cannotAddCopyCodeButton = this.cannotAddButton || copyCodeTypeNumber >= 1;

        this.computeButtonsFromButtonsGroupedTyped();
      })
    );

    const { template, code } = this.form.getRawValue();
    this.managePlaceholders(template.body);
    this.subscriptions.add(
      this.body.valueChanges.pipe(debounceTime(300)).subscribe(value => this.managePlaceholders(value))
    );

    const { header } = template;
    this.headerType = header?.type === 'text' ? 'text' : 'media';

    if (this.template) {
      this.isReadOnly = !this.template.isElementEditable;
      const templateResponse = (this.template.templates || []).find(t => t.code === code)?.template;

      if (templateResponse) {
        this.reinstantiateButtonsGroupedByType(templateResponse);
        this.form.get('template').patchValue(templateResponse);
        if (this.isReadOnly) {
          this.form.get('template').disable();
          // Buttons grouped are apart the main form. Disable them apart.
          this.quickReplyButtons.disable({ emitEvent: false });
          this.urlButtons.disable({ emitEvent: false });
          this.phoneNumberButtons.disable({ emitEvent: false });
          this.copyCodeButtons.disable({ emitEvent: false });
        }
      }
    } else {
      // we are displaying / switching between tabs
      this.reinstantiateButtonsGroupedByType(this.form.value.template);
    }

    this.whatsappTemplatesService.cleanInvalidAgentShortcutsFromQuickRepliesBS
      .pipe(filter(i => !!i))
      .subscribe((agentShortcutsSet: Set<string>) => {
        this.quickReplyButtons.controls.forEach(control => {
          const { payload } = control.value;
          if (payload && !agentShortcutsSet.has(payload)) {
            control.get('payload').reset();
          }
        });
      });
  }

  private handleTemplateFormForWhatsappCloudAPIAccount() {
    this.subscriptions.add(
      this.whatsappTemplatesService.createTemplateSiteIdUpdatedBS.subscribe(value => {
        if (value) {
          const site = this.sitesService.sitesV2Map.get(value);
          this.isWhatsappCloudAPI = site?.isCloudAPI;

          this.whatsappTemplatesService.canActivateLimitedTimeOfferBS.next(
            this.whatsappTemplatesService.canActivateLimitedTimeOfferBS.value
          );
          this.sections.forEach(section => {
            if (section.type === WhatsappTemplateButtonTypeEnum.COPY_CODE) {
              if (!this.isWhatsappCloudAPI) {
                section.titleKey = 'parameters/whatsapp-templates/content/buttons/add-copy-code-restricted';
              } else {
                section.titleKey = 'parameters/whatsapp-templates/content/buttons/add-copy-code';
              }
            }
          });

          this.urlButtons.controls.forEach(group => {
            const trackLinkLabelControl = group.get('track_link_label_id');
            if (!this.isWhatsappCloudAPI) {
              if (trackLinkLabelControl.hasValidator(Validators.required)) {
                trackLinkLabelControl.removeValidators(Validators.required);
              }
            } else {
              if (!trackLinkLabelControl.hasValidator(Validators.required)) {
                trackLinkLabelControl.addValidators(Validators.required);
              }
            }
            trackLinkLabelControl.updateValueAndValidity();
          });
        }
      })
    );

    this.subscriptions.add(
      this.whatsappTemplatesService.canActivateLimitedTimeOfferBS.subscribe(enabled => {
        const enableLimitedTimeOffer = this.isWhatsappCloudAPI && enabled;
        if (enableLimitedTimeOffer) {
          if (this.create) {
            this.form.get('template.limitedTimeOfferEnabled').enable();
          } else {
            this.form.get('template.limitedTimeOfferEnabled').disable();
          }
        } else {
          this.form.get('template.limited_time_offer').reset();
          this.form.get('template.limitedTimeOfferEnabled').reset();
          this.form.get('template.limited_time_offer').disable();
          this.form.get('template.limitedTimeOfferEnabled').disable();
        }
      })
    );

    this.subscriptions.add(
      this.form.get('template.limitedTimeOfferEnabled').valueChanges.subscribe(enabled => {
        this.limitedTimeOfferEnabled = enabled;
        if (enabled) {
          this.headerTypeChanged({ value: this.headerType }, { preserve: true });
          this.bodyLTOMaxlength = this.limitations.maxLength.bodyLTO;
          this.form.get('template.limited_time_offer').enable();
          this.form.get('template.header.text').reset();
          this.form.get('template.footer').reset();
          this.form.get('template.footer').disable();
          if (this.urlButtons?.length === 0) {
            // A LTO must have at least one URL.
            this.addUrlButton();
          }
          this.forceButtonsOrderForLimitedTimeOffer();
        } else {
          this.bodyLTOMaxlength = null;
          this.form.get('template.footer').enable();
          this.form.get('template.limited_time_offer').disable();
        }
      })
    );
  }

  private reinstantiateButtonsGroupedByType(templateResponse) {
    // Manage sections order
    const buttonsTypesSet = templateResponse.buttons.reduce((result, button) => {
      result.add(button.type);
      return result;
    }, new Set());
    this.buttonsTypesSet = new Set(buttonsTypesSet);

    // Add missing types in correct order.
    this.sections.forEach(section => buttonsTypesSet.add(section.type));
    // Apply the order
    const buttonsTypesOrder = [...buttonsTypesSet];
    this.sections.sort((a, b) => (buttonsTypesOrder.indexOf(b.type) > buttonsTypesOrder.indexOf(a.type) ? -1 : 1));

    templateResponse.buttons.forEach(button => {
      switch (button.type) {
        case WhatsappTemplateButtonTypeEnum.QUICK_REPLY:
          this.addQuickReplyButton(button);
          break;
        case WhatsappTemplateButtonTypeEnum.URL:
          this.addUrlButton(button);
          break;
        case WhatsappTemplateButtonTypeEnum.PHONE_NUMBER:
          this.addPhoneNumberButton(button);
          break;
        case WhatsappTemplateButtonTypeEnum.COPY_CODE:
          this.addCopyCodeButton(button);
          break;
        default:
          break;
      }
    });
  }

  private computeButtonsFromButtonsGroupedTyped() {
    // Mechanism to force update of validity.
    this.buttons.clear();
    this.sections.forEach(({ formArray }) =>
      (formArray.controls || []).forEach((formGroup: FormGroup) => {
        const value = formGroup.getRawValue();
        const { type } = value;
        let controlClone;
        switch (type) {
          case WhatsappTemplateButtonTypeEnum.QUICK_REPLY:
            controlClone = this.newQuickReplyButtonFormGroup(value);
            break;
          case WhatsappTemplateButtonTypeEnum.URL:
            controlClone = this.newUrlButtonFormGroup(value);
            break;
          case WhatsappTemplateButtonTypeEnum.PHONE_NUMBER:
            controlClone = this.newPhoneNumberButtonFormGroup(value);
            break;
          case WhatsappTemplateButtonTypeEnum.COPY_CODE:
            controlClone = this.newCopyCodeButtonFormGroup(value);
            break;
          default:
            break;
        }
        controlClone.patchValue(formGroup.getRawValue());
        this.buttons.push(controlClone);
      })
    );
  }

  private managePlaceholders(value: string) {
    /**
     * Contains the markers related to a placeholder.
     * @example
     * ['{{1}}', '{{2}}']
     */
    const placeholders: string[] = value?.match(new RegExp('{{[0-9]+}}', 'g')) || [];
    placeholders.forEach(this.updatePlaceholders);

    if (placeholders.length > this.limitations.maxNumber.placeholders) {
      this.body.setErrors({ tooManyPlaceholders: true }, { emitEvent: false });
    }
    Object.keys(this.placeholders.controls).forEach((controlName: string) => {
      if (!placeholders.includes(controlName)) {
        this.clearPlaceholder(controlName);
      }
    });
    this.placeholdersControls = Object.keys(this.placeholders.controls);
  }

  private updatePlaceholders = (label: string) => {
    if (!this.placeholders.get(label)) {
      this.placeholders.addControl(
        label,
        new FormGroup({
          label: new FormControl(null, Validators.required),
          example: new FormControl(null),
        })
      );
    }
  };

  private clearPlaceholder(controlName: string) {
    this.placeholders.removeControl(controlName);
  }

  addQuickReplyButton(button?) {
    this.quickReplyButtons.push(this.newQuickReplyButtonFormGroup(button));
  }

  addUrlButton(button?) {
    this.urlButtons.push(this.newUrlButtonFormGroup(button));
  }

  addPhoneNumberButton(button?) {
    this.phoneNumberButtons.push(this.newPhoneNumberButtonFormGroup(button));
  }

  addCopyCodeButton(button?) {
    this.copyCodeButtons.push(this.newCopyCodeButtonFormGroup(button));
  }

  private newQuickReplyButtonFormGroup(value): FormGroup {
    const group = new FormGroup({
      type: new FormControl(WhatsappTemplateButtonTypeEnum.QUICK_REPLY),
      text: new FormControl(null, [Validators.required, Validators.maxLength(this.limitations.maxLength.button)]),
      payload: new FormControl(null),
    });
    if (value) {
      group.patchValue(value);
    }
    // trick to avoid the update mode to enable the global save after thinking the form was modified.
    setTimeout(() => this.subscriptions.add(group.valueChanges.subscribe(() => this.form.markAsDirty())), 500);
    return group;
  }

  private newUrlButtonFormGroup(value): FormGroup {
    const group = new FormGroup({
      type: new FormControl(WhatsappTemplateButtonTypeEnum.URL),
      text: new FormControl(null, [Validators.required, Validators.maxLength(this.limitations.maxLength.button)]),
      url: new FormControl(null, [Validators.required, Validators.maxLength(this.limitations.maxLength.url)]),
      track_link_label_id: new FormControl(null, Validators.required),
      example: new FormControl(null, [Validators.required, Validators.maxLength(this.limitations.maxLength.example)]),
    });
    if (!this.isWhatsappCloudAPI) {
      group.get('track_link_label_id').removeValidators(Validators.required);
    }
    group.addValidators(UrlValidator.buttonUrlExampleValidator(this.limitations.maxLength.url));

    this.subscriptions.add(
      group.valueChanges.subscribe(({ url }) => {
        if (url && url.indexOf('{{1}}') > -1) {
          this.urlPlaceholders[url] = true;
          if (this.form.enabled) {
            group.controls.example.enable({ emitEvent: false });
          }
        } else {
          this.urlPlaceholders[url] = false;
          if (this.form.enabled) {
            group.controls.example.setValue(null, { emitEvent: false });
            group.controls.example.disable({ emitEvent: false });
            group.removeValidators(UrlValidator.buttonUrlExampleValidator(this.limitations.maxLength.url));
          }
        }
        const buttonUrlInvalid = group.hasError('button-url');
        const buttonUrlMaxLengthInvalid =
          group.hasError('button-url-max-length') || group.get('url').hasError('max-length');
        const buttonUrlFormatInvalid =
          !/^(https?:\/\/.)[-a-zA-Z0-9@:%._\\+~#=]{0,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\\+.~#?&//=]*)/.test(
            group.value.url
          );
        group.get('url').setErrors(
          buttonUrlFormatInvalid || buttonUrlInvalid || buttonUrlMaxLengthInvalid
            ? {
                'button-url-max-length': buttonUrlMaxLengthInvalid,
                'button-url-format': buttonUrlFormatInvalid,
                'button-url': buttonUrlInvalid,
              }
            : null
        );
      })
    );

    if (value) {
      group.patchValue(value);
    }

    setTimeout(() => this.subscriptions.add(group.valueChanges.subscribe(() => this.form.markAsDirty())), 500);
    return group;
  }

  getUrlPreview(urlGroup: FormGroup) {
    const { url, example } = urlGroup.value;
    if (url && example) {
      const urlExample = url.replace(/(\{\{1\}\})/gi, example);
      try {
        const result = new URL(urlExample);
        return result.href;
      } catch {
        return '';
      }
    }
  }

  private newPhoneNumberButtonFormGroup(value): FormGroup {
    const group = new FormGroup({
      type: new FormControl(WhatsappTemplateButtonTypeEnum.PHONE_NUMBER),
      text: new FormControl(null, [Validators.required, Validators.maxLength(this.limitations.maxLength.button)]),
      phone_number: new FormControl(null, Validators.required),
    });
    if (value) {
      group.patchValue(value);
    }
    setTimeout(() => this.subscriptions.add(group.valueChanges.subscribe(() => this.form.markAsDirty())), 500);
    return group;
  }

  private newCopyCodeButtonFormGroup(value): FormGroup {
    const group = new FormGroup({
      type: new FormControl(WhatsappTemplateButtonTypeEnum.COPY_CODE),
      text: new FormControl({
        value: this.basicService.translate('parameters/whatsapp-templates/content/buttons/label/copy-code'),
        disabled: true,
      }),
      example: new FormControl(null, [Validators.required, Validators.maxLength(15)]),
    });
    if (value) {
      group.patchValue(value);
    }
    setTimeout(() => this.subscriptions.add(group.valueChanges.subscribe(() => this.form.markAsDirty())), 500);
    return group;
  }

  deleteButton(id: number, type: WhatsappTemplateButtonTypeEnum) {
    switch (type) {
      case WhatsappTemplateButtonTypeEnum.QUICK_REPLY:
        this.quickReplyButtons.removeAt(id);
        break;
      case WhatsappTemplateButtonTypeEnum.URL:
        this.urlButtons.removeAt(id);
        break;
      case WhatsappTemplateButtonTypeEnum.PHONE_NUMBER:
        this.phoneNumberButtons.removeAt(id);
        this.phoneNumberObjectMap.delete(id);
        break;
      case WhatsappTemplateButtonTypeEnum.COPY_CODE:
        this.copyCodeButtons.removeAt(id);
        break;
    }
  }

  headerTypeChanged({ value }, options?: { preserve: boolean }) {
    this.form.markAsDirty();
    if (!options || !options.preserve) {
      this.form.get('template.header').reset();
    }
    if (value === 'text') {
      this.headerType = 'text';
      this.form.get('template.header.media_url').removeValidators(Validators.required);
    } else {
      this.headerType = 'media';
      this.form.get('template.header.media_url').addValidators(Validators.required);
    }
    this.form.get('template.header.type').setValue(this.headerType, { emitEvent: false });
    this.form.get('template.header.media_url').updateValueAndValidity();
  }

  phoneInputChanged(value: string, buttonIndex: number) {
    this.phoneNumberButtons.controls[buttonIndex].get('phone_number').setValue(value);
  }

  telInputObject(obj, buttonIndex: number) {
    this.phoneNumberObjectMap.set(buttonIndex, obj);
  }

  telInputInit(buttonIndex: number): string {
    return this.phoneNumberButtons.value[buttonIndex].phone_number;
  }

  isPhoneNumberButtonValid(buttonIndex: number) {
    const valid = this.phoneNumberObjectMap.get(buttonIndex)?.isValidNumber();
    this.phoneNumberButtons.controls[buttonIndex].get('phone_number').setErrors(
      valid
        ? null
        : {
            'invalid-number': true,
          }
    );
    return valid;
  }

  drop(event: CdkDragDrop<any[]>) {
    moveItemInArray(this.sections, event.previousIndex, event.currentIndex);
    this.computeButtonsFromButtonsGroupedTyped();
  }

  sortPredicate = (index: number, item: CdkDrag<any>) => {
    const { type } = item.data;
    if (type === WhatsappTemplateButtonTypeEnum.QUICK_REPLY) {
      // The button type QUICK_REPLY is either in firsts or in lasts.
      return [0, 3].includes(index);
    }
    const quickReplySectionIndex = this.sections.findIndex(s => s.type === WhatsappTemplateButtonTypeEnum.QUICK_REPLY);
    // The others types can not take the QUICK_REPLY button types positions (first or lasts).
    return index !== quickReplySectionIndex;
  };

  forceButtonsOrderForLimitedTimeOffer() {
    this.sections = [this.copyCodeSection, this.urlsSection, this.phoneNumberSection, this.quickRepliesSection];
    (this.form.value.template.buttons || []).sort((a, b) => {
      if (a.type === b.type) {
        return 0;
      }
      if ([a.type, b.type].includes(WhatsappTemplateButtonTypeEnum.COPY_CODE)) {
        return 3;
      }
      if ([a.type, b.type].includes(WhatsappTemplateButtonTypeEnum.URL)) {
        return 2;
      }
      if ([a.type, b.type].includes(WhatsappTemplateButtonTypeEnum.PHONE_NUMBER)) {
        return 1;
      }
      return -1;
    });
    this.computeButtonsFromButtonsGroupedTyped();
  }

  ngOnDestroy() {
    this.subscriptions?.unsubscribe();
  }
}
